import {
  UserService,
  type UserSvcPaginationMeta,
  type UserSvcUserListItem,
} from '@kp/rest-api-javascript-sdk';

import type { SortingFilter } from '../common';

/** Tenant user as GET /user/api/users lists it. */
export type User = UserSvcUserListItem;

/** Whether the account can sign in. */
export const UserStatus = {
  Active: 'active',
  Inactive: 'inactive',
} as const satisfies Record<string, User['status']>;
export type UserStatus = User['status'];

export type UserSort =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'createdAt'
  | 'status'
  | 'lastActiveAt';

export type UserFilters = SortingFilter<UserSort> & {
  roles?: string[];
  /** Case-insensitive match on first name, last name and email. */
  search?: string;
};

/**
 * List the tenant's users.
 *
 * The service sorts `status` active first and counts a user without
 * recorded activity as the least recently active; ties go by name.
 */
export const getUsers = async (
  filter: UserFilters = {},
): Promise<{ meta?: UserSvcPaginationMeta; data?: User[] }> =>
  UserService.getUsers({
    query: {
      skip: filter.skip,
      limit: filter.limit,
      sort: filter.sort,
      sortDir: filter.sortDir,
      roles: filter.roles,
      search: filter.search,
    },
  });

/**
 * Get details of a user
 *
 * @param userId       The id of a user that will be fetched
 */
export const getUser = (userId: string) =>
  UserService.getUsersByUserId({ path: { userId } });

/**
 * Remove a user from tenant and delete their invitation
 *
 * @param userId       The id of a user that will be removed
 */
export const deleteUser = (userId: string) =>
  UserService.deleteUsersByUserId({ path: { userId } });
